export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
}

export interface EmailValidation {
  isValid: boolean;
  error?: string;
}

/**
 * Validates password strength according to security requirements
 */
export const validatePassword = (password: string): PasswordValidation => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): EmailValidation => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  return { isValid: true };
};

/**
 * Validates password confirmation
 */
export const validatePasswordConfirmation = (password: string, confirmPassword: string): EmailValidation => {
  if (!confirmPassword.trim()) {
    return { isValid: false, error: 'Password confirmation is required' };
  }
  
  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }
  
  return { isValid: true };
};

/**
 * Gets password strength indicator
 */
export const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
  const validation = validatePassword(password);
  const errorCount = validation.errors.length;
  
  if (errorCount >= 3) return 'weak';
  if (errorCount >= 1) return 'medium';
  return 'strong';
};

/**
 * Sanitizes error messages from Supabase auth
 */
export const getAuthErrorMessage = (error: any): string => {
  if (!error) return 'An unexpected error occurred';
  
  const message = error.message || error.msg || error;
  
  // Common Supabase auth error mappings
  const errorMappings: Record<string, string> = {
    'Invalid login credentials': 'Invalid email or password. Please check your credentials and try again.',
    'Email not confirmed': 'Please check your email and click the confirmation link before signing in.',
    'Invalid email': 'Please enter a valid email address.',
    'Password should be at least 6 characters': 'Password must be at least 8 characters long.',
    'User already registered': 'An account with this email already exists. Please sign in instead.',
    'Signup is disabled': 'Account registration is currently disabled. Please contact support.',
    'Email rate limit exceeded': 'Too many emails sent. Please wait before requesting another email.',
    'For security purposes, you can only request this once every 60 seconds': 'Please wait 60 seconds before requesting another password reset email.',
  };
  
  // Check for exact matches first
  if (errorMappings[message]) {
    return errorMappings[message];
  }
  
  // Check for partial matches
  for (const [key, value] of Object.entries(errorMappings)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }
  
  // Return sanitized original message
  return message.charAt(0).toUpperCase() + message.slice(1);
}; 